/**
 * Type definitions for Memory Intelligence SDK
 */

import { z } from "zod";

// Response format enum
export const ResponseFormat = {
  JSON: "json",
  MARKDOWN: "markdown",
} as const;

export type ResponseFormatType = (typeof ResponseFormat)[keyof typeof ResponseFormat];

// Memory type enum
export const MemoryType = z.enum([
  "context",
  "project",
  "knowledge",
  "reference",
  "personal",
  "workflow",
]);

export type MemoryTypeValue = z.infer<typeof MemoryType>;

// Optional query-scope hint for context-aware rollouts.
export const QueryScope = z.enum([
  "personal",
  "team",
  "organization",
  "hybrid",
]);

export type QueryScopeValue = z.infer<typeof QueryScope>;

// Processing mode for SDK operations
export type ProcessingMode = 'api' | 'offline-fallback';

// Configuration interface
export interface MemoryIntelligenceConfig {
  apiUrl?: string; // Lanonasis API URL (default: https://api.lanonasis.com/api/v1)
  apiKey?: string; // Lanonasis API key (format: lano_xxxxxxxxxx)
  authToken?: string; // Bearer token for user-scoped auth (dashboard/SSO)
  authType?: 'apiKey' | 'bearer'; // Explicitly set auth type; inferred from apiKey/authToken if not provided
  allowMissingAuth?: boolean; // Allow client creation without auth (SDK consumers should gate calls)
  timeout?: number; // Request timeout in milliseconds (default: 30000)
  responseFormat?: ResponseFormatType;
  headers?: Record<string, string>; // Custom headers

  /**
   * Processing mode for intelligence operations
   * - 'api': Always use Lanonasis API endpoints (default)
   * - 'offline-fallback': Use API, fall back to cached/local data if unreachable
   * @default 'api'
   */
  processingMode?: ProcessingMode;

  /**
   * Enable caching of API responses for offline fallback
   * Only used when processingMode is 'offline-fallback'
   * @default true
   */
  enableCache?: boolean;

  /**
   * Cache TTL in milliseconds (default: 5 minutes)
   * @default 300000
   */
  cacheTTL?: number;
}

// API response wrapper from Edge Functions
export interface EdgeFunctionResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    message: string;
    code?: string;
  };
  usage?: {
    tokens_used: number;
    cost_usd: number;
    cached: boolean;
  };
  tier_info?: {
    tier: string;
    usage_remaining: number;
  };
}

export interface MemoryQueryContext {
  organizationId?: string;
  topicId?: string;
  memoryTypes?: MemoryTypeValue[];
  queryScope?: QueryScopeValue;
}

// Tool parameter interfaces
export interface AnalyzePatternsParams extends MemoryQueryContext {
  userId: string;
  timeRangeDays?: number;
  responseFormat?: ResponseFormatType;
}

export interface SuggestTagsParams {
  memoryId: string;
  userId: string;
  maxSuggestions?: number;
  includeExistingTags?: boolean;
  responseFormat?: ResponseFormatType;
}

export interface FindRelatedParams extends MemoryQueryContext {
  memoryId: string;
  userId: string;
  limit?: number;
  similarityThreshold?: number;
  responseFormat?: ResponseFormatType;
}

export interface DetectDuplicatesParams extends MemoryQueryContext {
  userId: string;
  similarityThreshold?: number;
  maxPairs?: number;
  responseFormat?: ResponseFormatType;
}

export interface ExtractInsightsParams extends MemoryQueryContext {
  userId: string;
  topic?: string;
  memoryType?: MemoryTypeValue;
  maxMemories?: number;
  responseFormat?: ResponseFormatType;
}

export interface HealthCheckParams extends MemoryQueryContext {
  userId: string;
  responseFormat?: ResponseFormatType;
}

// Tool result interfaces
export interface PatternAnalysis {
  total_memories: number;
  memories_by_type: Record<string, number>;
  memories_by_day_of_week: Record<string, number>;
  peak_creation_hours: number[];
  average_content_length: number;
  most_common_tags: Array<{ tag: string; count: number }>;
  creation_velocity: {
    daily_average: number;
    trend: "increasing" | "stable" | "decreasing";
  };
  insights: string[];
}

export interface TagSuggestion {
  tag: string;
  confidence: number;
  reasoning: string;
}

export interface TagSuggestionsResult {
  memory_id: string;
  existing_tags: string[];
  suggestions: TagSuggestion[];
  context: {
    memory_type: string;
    related_memories_analyzed: number;
  };
}

export interface RelatedMemory {
  id: string;
  title: string;
  type: string;
  similarity_score: number;
  shared_tags: string[];
  content_preview: string;
}

export interface RelatedMemoriesResult {
  source_memory: {
    id: string;
    title: string;
    type: string;
  };
  related_memories: RelatedMemory[];
  total_found: number;
}

export interface DuplicateEntry {
  id: string;
  title: string;
  similarity: number;
  created_at: string;
}

export interface DuplicateGroup {
  primary_id: string;
  primary_title: string;
  duplicates: DuplicateEntry[];
  similarity_score: number;
}

export interface DuplicatesResult {
  duplicate_groups: DuplicateGroup[];
  total_groups: number;
  total_duplicates: number;
  detection_method: "semantic" | "text";
  threshold_used: number;
  memories_analyzed: number;
  potential_storage_savings: string;
}

export interface Insight {
  type: "themes" | "connections" | "gaps" | "actions" | "summary";
  content: string;
  confidence: number;
  related_memory_ids: string[];
}

export interface InsightsResult {
  insights: Insight[];
  overall_summary: string;
  memories_analyzed: number;
  insight_types: string[];
  topic_filter?: string | null;
  generated_at: string;
}

export interface HealthScoreBreakdown {
  organization: number;
  tagging: number;
  recency: number;
  completeness: number;
  diversity: number;
}

export interface HealthScore {
  overall: number;
  breakdown: HealthScoreBreakdown;
}

export interface HealthStatistics {
  total_memories: number;
  active_memories: number;
  archived_memories: number;
  memories_with_tags: number;
  unique_tags: number;
  memory_types: number;
  recent_memories_30d: number;
  stale_memories_90d: number;
}

export interface HealthIssue {
  severity: "high" | "medium" | "low";
  category: string;
  description: string;
  affected_count: number;
  recommendation: string;
}

export interface MemoryHealth {
  health_score: HealthScore;
  status: "excellent" | "good" | "needs_attention" | "poor";
  statistics: HealthStatistics;
  issues: HealthIssue[];
  recommendations: string[];
  generated_at: string;
}

// Memory entry interface (from database)
export interface MemoryEntry {
  id: string;
  user_id: string;
  organization_id?: string | null;
  topic_id?: string | null;
  title: string;
  content: string;
  type: MemoryTypeValue;
  memory_type?: string | null;
  tags?: string[];
  metadata?: Record<string, any>;
  embedding?: number[];
  access_count?: number | null;
  last_accessed?: string | null;
  created_at: string;
  updated_at: string;
}

// Query options for internal use
export interface QueryMemoriesOptions extends MemoryQueryContext {
  type?: string;
  limit?: number;
  offset?: number;
  includeEmbeddings?: boolean;
}
